import { api } from "../../api/apiSlice";

// Technical Work (Complaint / Service Visit / Installation) - React port of
// the Flutter-only module (2026-09-08), against TexasAlarms.Api's already
// complete src/Technical/TechnicalController.cs. ZERO backend change: every
// endpoint below already existed and is already deployed.
//
// Started life here as a single read-only GET for the Dashboard's "Client
// Requests" card (sourceGroup=SERVICE, i.e. Complaints + Service Visits
// only - Installation work is project/order driven, not a customer
// "request"). That query is unchanged and still feeds the dashboard.
//
// SCOPE, per direct instruction: the web module is for MANAGERS - approve
// (push to technical), assign technicians, hold/resume, complete, verify &
// close, and read the full detail. Field activities stay Flutter-only and
// are deliberately NOT exposed here even though the endpoints exist:
//   - POST work/{guid}/check-in    (needs real on-site GPS + a scanned QR)
//   - PUT  work/{guid}/service-report (the technician's own readings/sign)
//   - POST work/{guid}/diagnosis   (recorded against a scanned product)
// Their RESULTS are rendered read-only on the detail screen, since verifying
// them is exactly what a manager logs in to do.
export const technicalApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Work list / detail ----
    getTechnicalWork: builder.query({
      query: (params) => ({ url: "/technical/work", params }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((w) => ({ type: "TechnicalWork", id: w.recordGuid })),
              { type: "TechnicalWork", id: "LIST" },
            ]
          : [{ type: "TechnicalWork", id: "LIST" }],
    }),
    getTechnicalWorkItem: builder.query({
      query: (recordGuid) => ({ url: `/technical/work/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "TechnicalWork", id: recordGuid }],
    }),
    // Installation jobs only: what the confirmed order sold (lines, who booked it,
    // when) - gated by the work item, not by Sales read. No prices.
    getTechnicalWorkOrder: builder.query({
      query: (recordGuid) => ({ url: `/technical/work/${recordGuid}/order` }),
    }),

    // ---- The admin "ready to push" queue (Technical.AssignToTechnical) ----
    // Sales-Order-sourced work parks at ON_HOLD/"NEW ORDER" until someone
    // explicitly pushes it; this is that queue.
    getTechnicalPendingAssignment: builder.query({
      query: (params) => ({ url: "/technical/pending-assignment", params }),
      providesTags: [{ type: "TechnicalPendingAssignment", id: "LIST" }],
    }),
    assignToTechnical: builder.mutation({
      query: (recordGuid) => ({ url: `/technical/work/${recordGuid}/assign-to-technical`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "TechnicalWork", id: recordGuid },
        { type: "TechnicalWork", id: "LIST" },
        { type: "TechnicalPendingAssignment", id: "LIST" },
        { type: "TechnicalWorkActivity", id: recordGuid },
      ],
    }),

    // ---- Creating work ----
    // A support call is taken at a desk, not in the field, so these two stay
    // in scope for web. The technician list is optional on both - supplying
    // it flips the new row straight to ASSIGNED server-side.
    logComplaint: builder.mutation({
      query: (body) => ({ url: "/technical/complaints", method: "POST", data: body }),
      invalidatesTags: [{ type: "TechnicalWork", id: "LIST" }, { type: "TechnicalDutyList", id: "LIST" }],
    }),
    logServiceVisit: builder.mutation({
      query: (body) => ({ url: "/technical/service-visits", method: "POST", data: body }),
      invalidatesTags: [{ type: "TechnicalWork", id: "LIST" }, { type: "TechnicalDutyList", id: "LIST" }],
    }),

    // ---- Pipeline actions ----
    startTechnicalWork: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/technical/work/${recordGuid}/start`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "TechnicalWork", id: recordGuid },
        { type: "TechnicalWork", id: "LIST" },
        { type: "TechnicalWorkActivity", id: recordGuid },
        { type: "TechnicalDutyList", id: "LIST" },
      ],
    }),
    setTechnicians: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/technical/work/${recordGuid}/technicians`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "TechnicalWork", id: recordGuid },
        { type: "TechnicalWork", id: "LIST" },
        { type: "TechnicalWorkActivity", id: recordGuid },
        { type: "TechnicalDutyList", id: "LIST" },
      ],
    }),
    holdTechnicalWork: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/technical/work/${recordGuid}/hold`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "TechnicalWork", id: recordGuid },
        { type: "TechnicalWork", id: "LIST" },
        { type: "TechnicalWorkActivity", id: recordGuid },
      ],
    }),
    resumeTechnicalWork: builder.mutation({
      query: (recordGuid) => ({ url: `/technical/work/${recordGuid}/resume`, method: "POST" }),
      invalidatesTags: (result, error, recordGuid) => [
        { type: "TechnicalWork", id: recordGuid },
        { type: "TechnicalWork", id: "LIST" },
        { type: "TechnicalWorkActivity", id: recordGuid },
      ],
    }),
    completeTechnicalWork: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/technical/work/${recordGuid}/complete`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "TechnicalWork", id: recordGuid },
        { type: "TechnicalWork", id: "LIST" },
        { type: "TechnicalWorkActivity", id: recordGuid },
        { type: "TechnicalDutyList", id: "LIST" },
      ],
    }),
    closeTechnicalWork: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/technical/work/${recordGuid}/close`, method: "POST", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "TechnicalWork", id: recordGuid },
        { type: "TechnicalWork", id: "LIST" },
        { type: "TechnicalWorkActivity", id: recordGuid },
      ],
    }),
    updateTechnicalWorkDescription: builder.mutation({
      query: ({ recordGuid, ...body }) => ({ url: `/technical/work/${recordGuid}/description`, method: "PUT", data: body }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "TechnicalWork", id: recordGuid },
        { type: "TechnicalWork", id: "LIST" },
        { type: "TechnicalWorkActivity", id: recordGuid },
      ],
    }),

    // ---- Activity timeline (Technical.ManageWork|Technical.CloseWork) ----
    // Supervisory-only by design - a technician login is not meant to see
    // the chain of hands, so the caller must gate on the same pair the API
    // does before rendering it.
    getTechnicalWorkActivity: builder.query({
      query: (recordGuid) => ({ url: `/technical/work/${recordGuid}/activity` }),
      providesTags: (result, error, recordGuid) => [{ type: "TechnicalWorkActivity", id: recordGuid }],
    }),

    // ---- Duty list ----
    // A caller without Technical.ManageWork is pinned to their own rows
    // server-side, so the employee filter below is only ever meaningful for
    // a manager - which is the only persona this web module serves.
    getTechnicalDutyList: builder.query({
      query: (params) => ({ url: "/technical/duty-list", params }),
      providesTags: [{ type: "TechnicalDutyList", id: "LIST" }],
    }),

    // ---- Technician Duty Report (Reports -> Technical Work, Batch159) ----
    // One row per (technician, job) for the range: attended jobs dated by
    // completion, still-open jobs carried forward. The page folds these into
    // both the per-technician summary and the per-job detail client-side.
    getTechnicalDutyReport: builder.query({
      query: (params) => ({ url: "/technical/duty-report", params }),
      providesTags: [{ type: "TechnicalDutyList", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTechnicalWorkQuery,
  useGetTechnicalWorkItemQuery,
  useGetTechnicalWorkOrderQuery,
  useGetTechnicalPendingAssignmentQuery,
  useAssignToTechnicalMutation,
  useLogComplaintMutation,
  useLogServiceVisitMutation,
  useStartTechnicalWorkMutation,
  useSetTechniciansMutation,
  useHoldTechnicalWorkMutation,
  useResumeTechnicalWorkMutation,
  useCompleteTechnicalWorkMutation,
  useCloseTechnicalWorkMutation,
  useUpdateTechnicalWorkDescriptionMutation,
  useGetTechnicalWorkActivityQuery,
  useGetTechnicalDutyListQuery,
  useGetTechnicalDutyReportQuery,
} = technicalApi;
